import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  LinearProgress,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import {
  Close,
  Refresh,
  Delete,
  Download,
  ErrorOutline,
  InfoOutlined,
  WarningAmber,
} from '@mui/icons-material';

import { api } from '../../services/api';
import type { Device } from '../../types';

interface DeviceLogsDialogProps {
  open: boolean;
  onClose: () => void;
  device: Device | null;
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  source: string;
}

const DeviceLogsDialog: React.FC<DeviceLogsDialogProps> = ({
  open,
  onClose,
  device,
}) => {
  const { t } = useTranslation();
  // State
  const [logType, setLogType] = useState<'all' | 'system' | 'player'>('all');
  const [lines, setLines] = useState(100);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Query для получения логов
  const {
    data: logsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['device-logs', device?.id, logType, lines, refreshCounter],
    queryFn: () => {
      if (!device) return null;
      return api.getDeviceLogs(device.id, { log_type: logType, lines, refresh: refreshCounter > 0 });
    },
    enabled: open && !!device,
    staleTime: 30000,
  });

  // Mutation для очистки логов
  const clearLogsMutation = useMutation({
    mutationFn: ({ deviceId, logType }: { deviceId: number; logType: 'all' | 'system' | 'player' }) => {
      return api.clearDeviceLogs(deviceId, { log_type: logType, confirm: true });
    },
    onSuccess: () => {
      // Обновляем логи после очистки
      refetch();
      setClearConfirmOpen(false);
    },
  });

  const handleRefresh = () => {
    setRefreshCounter((value) => value + 1);
  };

  const handleClearLogs = () => {
    if (device) {
      clearLogsMutation.mutate({ deviceId: device.id, logType });
    }
  };

  const getLogLevelIcon = (level: string) => {
    switch (level.toUpperCase()) {
      case 'ERROR':
        return <ErrorOutline color="error" fontSize="small" />;
      case 'WARNING':
        return <WarningAmber color="warning" fontSize="small" />;
      case 'INFO':
        return <InfoOutlined color="info" fontSize="small" />;
      default:
        return <InfoOutlined color="action" fontSize="small" />;
    }
  };

  const getLogLevelColor = (level: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (level.toUpperCase()) {
      case 'ERROR':
        return 'error';
      case 'WARNING':
        return 'warning';
      case 'INFO':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const exportLogs = () => {
    if (!logsData?.entries) return;

    const logText = logsData.entries
      .map(entry => `[${formatTimestamp(entry.timestamp)}] [${entry.level}] [${entry.source}] ${entry.message}`)
      .join('\n');

    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `device-${device?.id}-logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {t('devices.logs.title')} - {device?.name}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Управление */}
          {isLoading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary">
                Запрашиваем логи у клиента или читаем свежий серверный кеш...
              </Typography>
            </Box>
          )}

          <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('devices.logs.logType')}</InputLabel>
              <Select
                value={logType}
                label={t('devices.logs.logType')}
                onChange={(e) => setLogType(e.target.value as 'all' | 'system' | 'player')}
              >
                <MenuItem value="all">{t('devices.logs.logTypes.all')}</MenuItem>
                <MenuItem value="system">{t('devices.logs.logTypes.system')}</MenuItem>
                <MenuItem value="player">{t('devices.logs.logTypes.player')}</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="number"
              label={t('devices.logs.lines')}
              value={lines}
              onChange={(e) => setLines(Number(e.target.value))}
              inputProps={{ min: 10, max: 1000 }}
              sx={{ width: 120 }}
            />

            <Tooltip title={t('devices.logs.refresh')}>
              <IconButton onClick={handleRefresh} disabled={isLoading}>
                <Refresh />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('common.export')}>
              <IconButton onClick={exportLogs} disabled={!logsData?.entries?.length}>
                <Download />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('devices.logs.clearLogs')}>
              <IconButton 
                onClick={() => setClearConfirmOpen(true)} 
                disabled={clearLogsMutation.isPending}
                color="error"
              >
                <Delete />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Информация о логах */}
          {logsData && (
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ py: 1 }}>
                <Stack direction="row" spacing={3}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('devices.logs.logType')}:</strong> {t(`devices.logs.logTypes.${logType}`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('common.total')}:</strong> {logsData.total_lines}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>{t('common.retrieved')}:</strong> {formatTimestamp(logsData.retrieved_at)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Источник:</strong> {logsData.cached ? 'серверный кеш' : 'клиент'} {logsData.command_id ? `#${logsData.command_id}` : ''}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Логи */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('devices.logs.clearError')}: {error.message}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>{t('devices.logs.loading')}</Typography>
            </Box>
          ) : logsData?.entries?.length ? (
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="180px">{t('devices.logs.timestamp')}</TableCell>
                    <TableCell width="100px">{t('devices.logs.level')}</TableCell>
                    <TableCell width="100px">{t('devices.logs.source')}</TableCell>
                    <TableCell>{t('devices.logs.message')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logsData.entries.map((entry, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
                        {formatTimestamp(entry.timestamp)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getLogLevelIcon(entry.level)}
                          label={entry.level}
                          size="small"
                          color={getLogLevelColor(entry.level)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.source}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem', fontFamily: 'monospace' }}>
                        {entry.message}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {t('devices.logs.noLogs')}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения очистки */}
      <Dialog
        open={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('devices.logs.clearLogs')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('devices.logs.clearConfirm')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('devices.logs.logType')}: {t(`devices.logs.logTypes.${logType}`)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirmOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleClearLogs}
            color="error"
            variant="contained"
            disabled={clearLogsMutation.isPending}
          >
            {clearLogsMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t('devices.logs.clearLogs')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DeviceLogsDialog;
